import * as React from "react";
import {
  Box,
  Button,
  CircularProgress,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { supabase } from "../../logic/client";
import { useI18n } from "../../i18n/I18nProvider";

type LabScoreRow = {
  aware_points: number;
  prepare_points: number;
  lab_score: number;
  score_band: "Exposed" | "Developing" | "Operationally Ready";
  availability_signal: "High" | "Medium" | "Low" | null;
};

export default function AwareBaselineScoreScreen(props: {
  handlePageChange: (page: string) => void;
  nextPage?: string;
  backPage?: string;
}) {
  const {
    handlePageChange,
    nextPage = "VibeQuestions:prepare",
  } = props;
  const { lang } = useI18n();
  const isDE = lang === "de";

  const [score, setScore] = React.useState<LabScoreRow | null>(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    async function loadScore() {
      setLoading(true);

      const { data: userRes } = await supabase.auth.getUser();
      const userId = userRes.user?.id;

      if (!userId) {
        setLoading(false);
        return;
      }

      const { data: employee } = await supabase
        .from("employees")
        .select("id")
        .eq("auth_user_id", userId)
        .maybeSingle();

      if (!employee) {
        setLoading(false);
        return;
      }

      await supabase.rpc("recalculate_lab_score", {
        p_employee_id: employee.id,
      });

      const { data } = await supabase
        .from("lab_scores")
        .select("aware_points, prepare_points, lab_score, score_band, availability_signal")
        .eq("employee_id", employee.id)
        .maybeSingle();

      setScore(data as LabScoreRow | null);
      setLoading(false);
    }

    loadScore();
  }, []);

  const baseline = Number(score?.aware_points ?? 0);
  const baselineDisplay = baseline.toFixed(1);
  const progressValue = Math.min(100, Math.max(0, (baseline / 60) * 100));

  return (
    <Box
      sx={{
        minHeight: "calc(100vh - 48px)",
        width: "100%",
        px: { xs: 2, sm: 3 },
        py: { xs: 3, sm: 6 },
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background:
          "linear-gradient(135deg, rgba(16,16,18,1) 0%, rgba(48,27,27,1) 100%)",
      }}
    >
      <Paper
        sx={{
          width: { xs: "100%", sm: "min(88vw, 780px)", md: "min(78vw, 860px)" },
          borderRadius: 5,
          p: { xs: 4, sm: 6, md: 7 },
          border: "1px solid rgba(255,255,255,0.14)",
          background:
            "linear-gradient(180deg, rgba(49,32,32,0.95), rgba(34,21,21,0.94))",
          boxShadow: "0 24px 65px rgba(0,0,0,0.45)",
          textAlign: "center",
        }}
      >
        {loading ? (
          <Stack spacing={2.5} alignItems="center">
            <CircularProgress sx={{ color: "#d6293e" }} />
            <Typography sx={{ color: "rgba(255,255,255,0.82)" }}>
              {isDE ? "Lab Score wird berechnet..." : "Calculating your Lab Score..."}
            </Typography>
          </Stack>
        ) : (
          <Stack spacing={4} alignItems="center">
            <Typography
              sx={{
                color: "#fff",
                fontSize: { xs: "1.65rem", sm: "2.25rem" },
                fontWeight: 900,
                lineHeight: 1.2,
              }}
            >
              {isDE ? "Dein Ausgangspunkt ist klar." : "Your starting point is clear."}
            </Typography>

            <Typography
              sx={{
                color: "rgba(255,255,255,0.78)",
                maxWidth: 620,
                lineHeight: 1.65,
              }}
            >
              {isDE
                ? "BEWUSST hat deinen persönlichen Ausgangswert erstellt. In BEREIT kannst du denselben Lab Score jetzt durch praktische Stationen verbessern."
                : "AWARE has created your personal baseline. In PREPARE, you can now improve the same Lab Score through practical stations."}
            </Typography>

            <Box>
              <Typography
                sx={{
                  color: "#fff",
                  fontSize: { xs: "4rem", sm: "5.4rem" },
                  fontWeight: 950,
                  lineHeight: 1,
                }}
              >
                {baselineDisplay}
                <Typography
                  component="span"
                  sx={{
                    color: "rgba(255,255,255,0.58)",
                    fontSize: { xs: "1.8rem", sm: "2.2rem" },
                    fontWeight: 800,
                  }}
                >
                  /60
                </Typography>
              </Typography>

              <Typography
                sx={{
                  color: "rgba(255,255,255,0.68)",
                  mt: 1,
                  fontWeight: 700,
                }}
              >
                {isDE ? "Baseline Lab Score" : "Baseline Lab Score"}
              </Typography>
            </Box>

            <Box sx={{ width: "100%", maxWidth: 560 }}>
              <LinearProgress
                variant="determinate"
                value={progressValue}
                sx={{
                  height: 12,
                  borderRadius: 999,
                  backgroundColor: "rgba(255,255,255,0.14)",
                  "& .MuiLinearProgress-bar": {
                    borderRadius: 999,
                    background: "linear-gradient(90deg, #b50d20, #d6293e)",
                  },
                }}
              />
            </Box>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              sx={{ width: "100%", maxWidth: 620 }}
            >
              <Box
                sx={{
                  flex: 1,
                  p: 2,
                  borderRadius: 3,
                  backgroundColor: "rgba(255,255,255,0.06)",
                  border: "1px solid rgba(255,255,255,0.12)",
                }}
              >
                <Typography sx={{ color: "rgba(255,255,255,0.58)", fontWeight: 700 }}>
                  {isDE ? "Band" : "Band"}
                </Typography>
                <Typography sx={{ color: "#fff", fontWeight: 900 }}>
                  {score?.score_band ?? "Exposed"}
                </Typography>
              </Box>

              <Box
                sx={{
                  flex: 1,
                  p: 2,
                  borderRadius: 3,
                  backgroundColor: "rgba(255,255,255,0.06)",
                  border: "1px solid rgba(255,255,255,0.12)",
                }}
              >
                <Typography sx={{ color: "rgba(255,255,255,0.58)", fontWeight: 700 }}>
                  {isDE ? "Verfügbarkeit" : "Availability"}
                </Typography>
                <Typography sx={{ color: "#fff", fontWeight: 900 }}>
                  {score?.availability_signal ?? "Low"}
                </Typography>
              </Box>
            </Stack>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} justifyContent="center">
              <Button
                variant="outlined"
                onClick={() => handlePageChange("AwareIntroScreen")}
                sx={{
                  borderRadius: 999,
                  textTransform: "none",
                  fontWeight: 900,
                  px: 4,
                  py: 1.25,
                  borderColor: "rgba(255,255,255,0.4)",
                  color: "#fff",
                  "&:hover": {
                    borderColor: "#fff",
                    backgroundColor: "rgba(255,255,255,0.08)",
                  },
                }}
              >
                {isDE ? "BEWUSST erneut machen" : "Retake AWARE section"}
              </Button>

              <Button
                variant="contained"
                onClick={() => handlePageChange(nextPage)}
                sx={{
                  borderRadius: 999,
                  textTransform: "none",
                  fontWeight: 900,
                  px: 4,
                  py: 1.25,
                  background: "linear-gradient(90deg, #b50d20, #d6293e)",
                  "&:hover": {
                    background: "linear-gradient(90deg, #9f0b1c, #c32136)",
                  },
                }}
              >
                {nextPage === "JourneyPillars"
                  ? isDE
                    ? "Zurück zur Homepage"
                    : "Back to Homepage"
                  : isDE
                    ? "Weiter zu BEREIT"
                    : "Continue to PREPARE"}
              </Button>
            </Stack>
          </Stack>
        )}
      </Paper>
    </Box>
  );
}
